import { useState, useEffect } from "react";
import PropTypes from 'prop-types';
import { toast } from "react-hot-toast";
import axios from "axios";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, Mail, MessageCircle } from "lucide-react";
import { Separator } from "@/components/ui/separator";

export default function ScheduleMeetingModal({ open, onOpenChange, selectedBatches, courseName }) {
  const [date, setDate] = useState(new Date());
  const [time, setTime] = useState("");
  const [mentor, setMentor] = useState("");
  const [agenda, setAgenda] = useState("");
  const [mentors, setMentors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [scheduling, setScheduling] = useState(false);
  const [sendEmailNotifications, setSendEmailNotifications] = useState(true);
  const [sendWhatsAppNotifications, setSendWhatsAppNotifications] = useState(true);

  useEffect(() => {
    const fetchMentors = async () => {
      try {
        setLoading(true);
        const response = await axios.get('https://66z6lhu4ge.execute-api.ap-south-1.amazonaws.com/prod/zoom/users');
        setMentors(response.data.users || []);
      } catch (error) {
        console.error('Error fetching mentors:', error);
        toast.error('Failed to fetch mentors');
        setMentors([]);
      } finally {
        setLoading(false);
      }
    };

    if (open) {
      fetchMentors();
    }
  }, [open]);

  useEffect(() => {
    if (open) {
      // Set body attribute to track modal state
      document.body.setAttribute('data-modal-open', 'true');
      document.body.style.overflow = 'hidden';
      
      setTimeout(() => {
        const firstInput = document.querySelector('#agenda');
        if (firstInput) {
          firstInput.focus();
        }
      }, 100);
    } else {
      // Cleanup is now handled by handleModalClose
      setScheduling(false);
      setLoading(false);
    }
  }, [open]);

  // ✅ ADDED: Cleanup on unmount
  useEffect(() => {
    return () => {
      // Ensure cleanup on component unmount
      document.body.removeAttribute('data-modal-open');
      document.body.style.overflow = 'auto';
      document.body.style.pointerEvents = 'auto';
    };
  }, []);

  // ✅ FIXED: Custom modal close handler with robust cleanup
  const handleModalClose = (newOpen) => {
    if (!newOpen) {
      // Reset all form state
      setDate(new Date());
      setTime("");
      setMentor("");
      setAgenda("");
      setScheduling(false);
      setLoading(false);
      setSendEmailNotifications(true);
      setSendWhatsAppNotifications(true);
      
      // Remove body attribute and restore styles
      document.body.removeAttribute('data-modal-open');
      document.body.style.overflow = 'auto';
      document.body.style.pointerEvents = 'auto';
      
      // Force cleanup of any trapped focus
      setTimeout(() => {
        // Remove any focus traps
        const activeElement = document.activeElement;
        if (activeElement && (activeElement.closest('[role="dialog"]') || activeElement.closest('[data-radix-dialog-overlay]'))) {
          document.body.focus();
        }
        
        // Remove any remaining overlay elements
        const overlays = document.querySelectorAll('[data-radix-dialog-overlay]');
        overlays.forEach(overlay => {
          if (overlay.getAttribute('data-state') === 'closed') {
            overlay.remove();
          }
        });
        
        // Force pointer events to be enabled on body
        document.body.style.pointerEvents = 'auto';
        document.body.style.overflow = 'auto';
      }, 100);
    }
    onOpenChange(newOpen);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (selectedBatches.length === 0) {
      toast.error("Please select at least one batch");
      return;
    }

    if (!mentor || !date || !time || !agenda) {
      toast.error("Please fill in all required fields");
      return;
    }

    // Create the date in IST timezone
    const selectedDate = new Date(date);
    const [hours, minutes] = time.split(':');
    
    // Set the time in IST (UTC+5:30)
    selectedDate.setHours(parseInt(hours), parseInt(minutes), 0, 0);
    
    // Convert IST to UTC by adding 5 hours and 30 minutes
    const istOffset = 5.5 * 60 * 60 * 1000; // 5.5 hours in milliseconds
    const utcDate = new Date(selectedDate.getTime() + istOffset);
    
    // Validate time is not in the past (compare with current IST time)
    const now = new Date();
    const nowIST = new Date(now.getTime() + istOffset);
    
    if (selectedDate < nowIST) {
      toast.error("Meeting time must be in the future");
      return;
    }
    
    try {
      setScheduling(true);
      
      const selectedMentor = mentors.find(m => m.id === mentor);
      if (!selectedMentor?.email) {
        toast.error("Invalid mentor selected");
        return;
      }

      // Format the request data with UTC time and notification options
      const requestData = {
        mentorName: selectedMentor.email,
        meetingTime: utcDate.toISOString(),
        agenda: agenda.trim(),
        batchNames: selectedBatches.map(batch => batch.trim()),
        courseName: courseName,
        sendEmailNotifications,
        sendWhatsAppNotifications
      };
      
      // Schedule the meeting using our API
      await axios.post(
        'https://66z6lhu4ge.execute-api.ap-south-1.amazonaws.com/prod/zoom/schedule-meeting', 
        requestData
      );

      // Show success message immediately
      toast.success(`Meeting scheduled successfully for ${selectedBatches.length} batch(es)!`);
      
      // Show notification status
      if (sendEmailNotifications || sendWhatsAppNotifications) {
        toast.success("Notifications are being sent in the background");
      }

      handleModalClose(false);
    } catch (error) {
      console.error('Error scheduling meeting:', error.response?.data || error);
      const errorMessage = error.response?.data?.error || 'Failed to schedule meeting. Please try again.';
      toast.error(errorMessage);
    } finally {
      setScheduling(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleModalClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold">
            Schedule Meeting for {selectedBatches.length} Batch(es)
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Meeting Details Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">Meeting Details</h3>
            
            <div className="space-y-2">
              <Label htmlFor="agenda" className="text-sm font-medium">Meeting Agenda *</Label>
              <Textarea
                id="agenda"
                value={agenda}
                onChange={(e) => setAgenda(e.target.value)}
                placeholder="Enter meeting agenda..."
                className="min-h-[80px]"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="mentor" className="text-sm font-medium">Select Mentor *</Label>
              <Select value={mentor} onValueChange={setMentor} required>
                <SelectTrigger id="mentor">
                  <SelectValue placeholder={loading ? "Loading mentors..." : "Select a mentor"} />
                </SelectTrigger>
                <SelectContent>
                  {loading ? (
                    <div className="flex items-center justify-center p-4">
                      <Loader2 className="h-6 w-6 animate-spin" />
                    </div>
                  ) : mentors.length > 0 ? (
                    mentors.map((mentor) => (
                      <SelectItem key={mentor.id} value={mentor.id}>
                        {mentor.display_name}
                      </SelectItem>
                    ))
                  ) : (
                    <div className="p-2 text-sm text-gray-500">No mentors available</div>
                  )}
                </SelectContent>
              </Select>
            </div>

            {/* Date and Time Selection - Stacked on mobile, side by side on larger screens */}
            <div className="space-y-4 sm:space-y-0 sm:grid sm:grid-cols-2 sm:gap-4">
              <div className="space-y-2">
                <Label className="text-sm font-medium">Select Date *</Label>
                <div className="relative">
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={setDate}
                    className="rounded-md border w-full"
                    classNames={{
                      months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
                      month: "space-y-4",
                      caption: "flex justify-center pt-1 relative items-center",
                      caption_label: "text-sm font-medium",
                      nav: "space-x-1 flex items-center",
                      nav_button: "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100",
                      nav_button_previous: "absolute left-1",
                      nav_button_next: "absolute right-1",
                      table: "w-full border-collapse space-y-1",
                      head_row: "flex",
                      head_cell: "text-muted-foreground rounded-md w-8 font-normal text-[0.8rem]",
                      row: "flex w-full mt-2",
                      cell: "text-center text-sm p-0 relative [&:has([aria-selected])]:bg-accent first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20",
                      day: "h-8 w-8 p-0 font-normal aria-selected:opacity-100",
                      day_selected: "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground",
                      day_today: "bg-accent text-accent-foreground",
                      day_outside: "text-muted-foreground opacity-50",
                      day_disabled: "text-muted-foreground opacity-50",
                      day_range_middle: "aria-selected:bg-accent aria-selected:text-accent-foreground",
                      day_hidden: "invisible",
                    }}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="time" className="text-sm font-medium">Select Time (IST) *</Label>
                <Input
                  id="time"
                  type="time"
                  value={time}
                  onChange={(e) => setTime(e.target.value)}
                  className="w-full"
                  required
                />
                <p className="text-xs text-muted-foreground">
                  All times are in Indian Standard Time (IST)
                </p>
              </div>
            </div>
          </div>

          <Separator />

          {/* Notification Options Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">Notification Options</h3>
            <p className="text-sm text-muted-foreground">
              Choose how you want to notify students about this meeting
            </p>
            
            <div className="space-y-3">
              <div className="flex items-center space-x-3">
                <Checkbox
                  id="email-notifications"
                  checked={sendEmailNotifications}
                  onCheckedChange={setSendEmailNotifications}
                />
                <div className="flex items-center space-x-2">
                  <Mail className="h-4 w-4 text-blue-600" />
                  <Label htmlFor="email-notifications" className="text-sm font-medium cursor-pointer">
                    Send Email Notifications
                  </Label>
                </div>
              </div>
              
              <div className="flex items-center space-x-3">
                <Checkbox
                  id="whatsapp-notifications"
                  checked={sendWhatsAppNotifications}
                  onCheckedChange={setSendWhatsAppNotifications}
                />
                <div className="flex items-center space-x-2">
                  <MessageCircle className="h-4 w-4 text-green-600" />
                  <Label htmlFor="whatsapp-notifications" className="text-sm font-medium cursor-pointer">
                    Send WhatsApp Notifications
                  </Label>
                </div>
              </div>
            </div>

            {!sendEmailNotifications && !sendWhatsAppNotifications && (
              <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                <p className="text-sm text-yellow-800">
                  ⚠️ No notifications will be sent. Students won&apos;t be informed about this meeting.
                </p>
              </div>
            )}
          </div>

          <DialogFooter className="pt-4">
            <Button type="button" variant="outline" onClick={() => handleModalClose(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={scheduling} className="min-w-[140px]">
              {scheduling ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Scheduling...
                </>
              ) : (
                'Schedule Meeting'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

ScheduleMeetingModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  selectedBatches: PropTypes.arrayOf(PropTypes.string).isRequired,
  courseName: PropTypes.string.isRequired
}; 